import React, { useContext } from "react";
import {
  Box,
  Button,
  makeStyles,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  IconButton,
} from "@material-ui/core";
import { Cached as CachedIcon } from "@material-ui/icons";
import { StyledTableCell, StyledTableRow } from "components/table";
import { getLang } from "app/feature/constants";
import { PrizeContext } from "../../dialog/tranditionalRaffleWinnerDialog/tranditionalRaffleWinnerDialog.container";
import { DustBinSVG } from "../../../utils/static";
import SetTranditionalRaffleWinnerSelect from "../../select/tranditionalRaffleWinnerSelect";

const useStyle = makeStyles((theme) => ({
  table: {
    tableLayout: "auto",
    whiteSpace: "nowrap",
    borderCollapse: "separate",
    [theme.breakpoints.down("xs")]: {
      minWidth: 0,
    },
    "& th,td": {
      fontSize: ".75rem",
      padding: theme.spacing(1, 2, 1, 2),
    },
    "& th": {
      color: theme.palette.text.secondary,
      fontWeight: theme.typography.fontWeightBold,
    },
    "& th:last-child, td:last-child": {
      width: 120,
    },
  },
  tableContainer: {
    maxHeight: 500,
    overflowY: "auto",
  },
  emptyContainer: {
    backgroundColor: theme.palette.background.paper,
    textAlign: "center",
  },
  selectCell: {
    minWidth: 200,
  },
  actionCell: {
    minWidth: 80,
  },
}));

export default function EditTranditionalRaffleWinnerTableComponent({
  data,
  lang,
  prizeIndex,
  hasEligibleParticipants,
  availableWinnersCount,
}) {
  const classes = useStyle();
  const {
    handleDelete,
    handleRandomizeForPrize,
    handleRandomizeIndividualWinner,
  } = useContext(PrizeContext);

  return (
    <Box p={2}>
      {hasEligibleParticipants && (
        <Box mb={2} display="flex" justifyContent="flex-end">
          <Button
            variant="outlined"
            color="primary"
            size="small"
            onClick={() => handleRandomizeForPrize(prizeIndex)}
          >
            {getLang(lang, "label.RANDOMIZED_OF_PRIZE")} (
            {availableWinnersCount})
          </Button>
        </Box>
      )}

      <TableContainer
        className={classes.tableContainer}
        style={{ borderRadius: "10px" }}
      >
        <Table className={classes.table} size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <StyledTableCell>{getLang(lang, "label.WINNER")}</StyledTableCell>
              <StyledTableCell className={classes.selectCell}>
                {getLang(lang, "label.PARTICIPANT_SELECTION")}
              </StyledTableCell>
              <StyledTableCell>
                {getLang(lang, "label.SERIAL_NUMBER")}
              </StyledTableCell>
              <StyledTableCell>{getLang(lang, "label.NAME")}</StyledTableCell>
              <StyledTableCell>
                {getLang(lang, "label.PHONE_NUMBER_LAST_FOUR_DIGIT")}
              </StyledTableCell>
              <StyledTableCell
                className={classes.actionCell}
                style={{ textAlign: "center" }}
              >
                {getLang(lang, "label.ACTIONS")}
              </StyledTableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data.map((winner, index) => (
              <StyledTableRow key={index}>
                <StyledTableCell>
                  <Typography
                    variant="body2"
                    color="primary"
                    style={{ fontWeight: "bold" }}
                  >
                    {index + 1}
                    <span
                      style={{
                        color: "#DE350B",
                        display: "inline-block",
                        marginLeft: 4,
                      }}
                    ></span>
                  </Typography>
                </StyledTableCell>
                <StyledTableCell className={classes.selectCell}>
                  <SetTranditionalRaffleWinnerSelect
                    prizeIndex={prizeIndex}
                    winnerIndex={index}
                    value={winner}
                  />
                </StyledTableCell>
                <StyledTableCell>
                  <Typography variant="body2">
                    {winner.serial_number || "-"}
                  </Typography>
                </StyledTableCell>
                <StyledTableCell>
                  <Typography variant="body2">
                    {winner.participant?.name || "-"}
                  </Typography>
                </StyledTableCell>
                <StyledTableCell>
                  <Typography variant="body2">
                    {(() => {
                      const p = winner.participant;
                      if (!p) return "-";

                      // Try all possible phone number fields
                      const phone =
                        p.contact_no ||
                        p.contactNumber ||
                        p.phone ||
                        p.phone_no ||
                        p.phoneNo ||
                        p.mobile ||
                        p.mobile_no ||
                        p.mobileNo;

                      if (
                        phone &&
                        typeof phone === "string" &&
                        phone.length >= 4
                      ) {
                        return phone.slice(-4);
                      }
                      return "-";
                    })()}
                  </Typography>
                </StyledTableCell>
                <StyledTableCell style={{ textAlign: "center" }}>
                  <Box
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                  >
                    {!winner.status && hasEligibleParticipants && (
                      <IconButton
                        style={{ padding: 4, marginRight: 4 }}
                        onClick={() => {
                          handleRandomizeIndividualWinner(prizeIndex, index);
                        }}
                        title="Randomize this winner"
                      >
                        <CachedIcon fontSize="small" />
                      </IconButton>
                    )}
                    {!winner.status && (
                      <IconButton
                        style={{ padding: 4 }}
                        onClick={() => {
                          handleDelete(
                            prizeIndex,
                            index,
                            winner.serial_number,
                            winner.enc
                          );
                        }}
                        title="Clear this winner"
                      >
                        <DustBinSVG />
                      </IconButton>
                    )}
                  </Box>
                </StyledTableCell>
              </StyledTableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}
